import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { commessaCodiceRe, commessaEsempio, setCommessaFormat } from "@/lib/commessaCodice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import EmployeeAutocomplete from "@/components/EmployeeAutocomplete";
import CommessaCombobox from "@/components/CommessaCombobox";
import OcrConfirmDialog from "@/components/OcrConfirmDialog";
import OcrSourceGallery from "@/components/OcrSourceGallery";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { ViewerUnlockCard } from "@/components/ViewerUnlock";
import { EmployeeTotals } from "@/components/EmployeeTotals";
import {
  ArrowLeft,
  AlertTriangle,
  Plus,
  Trash2,
  ChevronUp,
  ChevronRight,
  ChevronDown,
  Save,
  Printer,
  Calendar as CalIcon,
  Camera,
  PenLine,
  Mail,
  CheckCircle2,
  Send,
  CalendarRange,
  Sparkles,
  AlertCircle,
  MapPin,
  Flame,
  Moon,
  CopyPlus,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { UnsavedExitDialog } from "@/components/UnsavedExitDialog";
import { Switch } from "@/components/ui/switch";

const newRiga = () => ({
  _uid: Math.random().toString(36).slice(2),
  commessa_codice: "",
  commessa_id: null,
  employee_nome: "",
  employee_id: null,
  ore_guida: 0,
  ore_passegg: 0,
  ore_lavoro: 0,
  ore_caldo: 0,
  ore_notturne: 0,
  ore_tot: 0,
  note: "",
});

const emptyDay = (data = "") => ({
  data,
  righe: [newRiga()],
  annotazioni: "",
  tot_pasti: 0,
  pasti_dettaglio: "",
  mezzi_guidatori: "",
  pranzo: "",
  pernotto: "",
});

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Iter 49b: una bozza ha senso solo se il form contiene davvero qualcosa
// (evita bozze "fantasma" create solo aprendo la pagina).
const formHasContent = (f) =>
  (f?.giorni || []).some(
    (g) =>
      (g.annotazioni || "").trim() ||
      (g.righe || []).some(
        (r) =>
          (r.commessa_codice || "").trim() ||
          (r.employee_nome || "").trim() ||
          Number(r.ore_tot || 0) > 0
      )
  );

function dayLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${DAY_NAMES[(d.getDay() + 6) % 7]} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// FIX iter 30i: bug timezone.
// `Date.toISOString()` ritorna UTC: in fuso UTC+1 una mezzanotte locale
// diventa 23:00 del giorno PRIMA in UTC e perde 1 giorno.
// Usiamo i componenti locali per ottenere sempre la data del fuso utente.
const toLocalISO = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayIso = () => toLocalISO(new Date());

function generateDays(dataInizio, dataFine) {
  const out = [];
  if (!dataInizio || !dataFine) return out;
  const start = new Date(dataInizio + "T00:00:00");
  const end = new Date(dataFine + "T00:00:00");
  if (end < start) return [dataInizio];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(toLocalISO(cur));  // local, niente shift UTC
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function RapportinoFormPage() {
  const { t } = useTranslation("app");
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isViewer = user?.role === "viewer";
  const ocrData = location.state?.ocr || null;
  // Provenienza: se ?from=da-vidimare la freccia indietro/Annulla torna
  // alla lista "Da vidimare" invece che alla pagina settimana.
  const backTo =
    new URLSearchParams(location.search).get("from") === "da-vidimare"
      ? "/da-vidimare"
      : "/";
  // Holds OCR payload while waiting for the user to confirm before pre-fill
  const [pendingOcr, setPendingOcr] = useState(ocrData);
  const [ocrConfirmed, setOcrConfirmed] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [ocrInfo, setOcrInfo] = useState(ocrData ? {
    matched_employees: ocrData.matches?.employees || [],
    matched_commesse: ocrData.matches?.commesse || [],
    warnings: ocrData.warnings || [],
  } : null);

  const [form, setForm] = useState({
    numero: 1,
    tipo: "cantiere", // "cantiere" | "officina"
    data_inizio: todayIso(),
    data_fine: todayIso(),
    giorni: [emptyDay(todayIso())],
    foto: [],
    firma: "",
    firma_data: "",
    firma_immagine: "",
  });
  const [ocrSources, setOcrSources] = useState([]);
  // Iter 103: avviso duplicati in tempo reale (stesso dipendente + stesso
  // giorno già presente in un altro rapportino). Solo avviso, non blocca.
  const [dupWarn, setDupWarn] = useState({});
  // Iter 31nn: true se il rapportino caricato è GIÀ vidimato (alert su modifica)
  const [loadedVidimato, setLoadedVidimato] = useState(false);
  // Iter 31nn: bozza automatica anti-perdita lavoro (auto-logout, chiusura…)
  const [draftChecked, setDraftChecked] = useState(false);
  // Iter 49b: dopo un salvataggio riuscito la bozza è consumata → stop sync
  const draftDoneRef = useRef(false);
  // Iter 143: modifiche non salvate (in edit: dopo il caricamento iniziale)
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const formInitRef = useRef(false);
  const DRAFT_KEY = "rapportino_draft_v1";

  // Ripristino bozza: solo su NUOVO rapportino "pulito" (no OCR, no ?data=)
  // Iter 43: la bozza vive anche sul server (visibile agli admin). Con
  // ?bozza=server (bottone "Riprendi" in Lista) si ripristina senza chiedere.
  useEffect(() => {
    // Iter 97: ricarica il formato commessa all'apertura del form (la sigla
    // può essere cambiata in Impostazioni durante la sessione)
    api.get("/commesse/formato").then(({ data }) => setCommessaFormat(data)).catch(() => {});
  }, []);

  // Iter 103: chiave univoca (giorno + dipendente) per mappare gli avvisi
  // duplicato alle righe del form.
  const dupKeyOf = (data, riga) => {
    const emp = riga.employee_id || (riga.employee_nome || "").trim().toLowerCase();
    if (!data || !emp) return null;
    return `${data}||${emp}`;
  };

  // Iter 103: controllo duplicati in tempo reale mentre si compila.
  // Raccoglie le coppie (dipendente, giorno) valorizzate e interroga
  // /rapportini/duplicates-check SENZA commessa → match a livello di
  // giornata (stesso dipendente ha già un rapportino per quel giorno).
  useEffect(() => {
    if (isViewer) return;
    const pairs = [];
    const seen = new Set();
    for (const g of form.giorni || []) {
      for (const r of g.righe || []) {
        const k = dupKeyOf(g.data, r);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        pairs.push({
          employee_id: r.employee_id || null,
          employee_nome: r.employee_nome || null,
          data: g.data,
        });
      }
    }
    if (!pairs.length) {
      setDupWarn({});
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.post("/rapportini/duplicates-check", {
          pairs,
          exclude_rapportino_id: id || null,
        });
        // Iter 107: per ogni (giorno+dipendente) → [{numero, ore}] con le ore
        // già registrate in ciascun altro rapportino (somma delle sue righe).
        const map = {};
        for (const d of data?.duplicates || []) {
          const emp = d.employee_id || (d.employee_nome || "").trim().toLowerCase();
          const k = `${d.data}||${emp}`;
          if (!map[k]) map[k] = [];
          const hit = map[k].find((x) => x.rapportino_id === d.rapportino_id);
          if (hit) hit.ore += Number(d.ore_tot || 0);
          else map[k].push({ rapportino_id: d.rapportino_id, numero: d.numero, ore: Number(d.ore_tot || 0) });
        }
        setDupWarn(map);
      } catch (e) {
        console.warn("[rapportino] dup realtime check:", e?.message);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [form.giorni, id, isViewer]);

  // Iter 119: coerenza rapportino ↔ Programma giornaliero (avviso + "Conferma" che sposta la persona nel programma)
  const [progWarn, setProgWarn] = useState({});
  const [progRecheck, setProgRecheck] = useState(0);
  // Opzione "Sincronizza col programma": disattiva di default, ricordata sul dispositivo
  const [progSync, setProgSync] = useState(() => { try { return localStorage.getItem("rapportino_prog_sync") === "1"; } catch (_) { return false; } });
  const toggleProgSync = (v) => { setProgSync(v); try { localStorage.setItem("rapportino_prog_sync", v ? "1" : "0"); } catch (_) { /* ignore */ } };
  const progKeyOf = (data, riga) => {
    const c = (riga.commessa_codice || "").trim().toUpperCase();
    return data && riga.employee_id && c ? `${data}||${riga.employee_id}||${c}` : null;
  };
  useEffect(() => {
    if (isViewer || !progSync) { setProgWarn({}); return; }
    const righe = [];
    const seen = new Set();
    for (const g of form.giorni || []) {
      for (const r of g.righe || []) {
        const k = progKeyOf(g.data, r);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        righe.push({ data: g.data, employee_id: r.employee_id, commessa_codice: (r.commessa_codice || "").trim() });
      }
    }
    if (!righe.length) { setProgWarn({}); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post("/programma/check-rapportino", { righe });
        const map = {};
        for (const r of data?.righe || []) {
          if (r.stato !== "ok") map[`${r.data}||${r.employee_id}||${(r.commessa_codice || "").trim().toUpperCase()}`] = r;
        }
        setProgWarn(map);
      } catch (e) {
        console.warn("[rapportino] programma check:", e?.message);
      }
    }, progRecheck ? 150 : 600);
    return () => clearTimeout(h);
  }, [form.giorni, isViewer, progRecheck, progSync]);
  const allineaProgramma = async (w) => {
    const k = `${w.data}||${w.employee_id}||${(w.commessa_codice || "").trim().toUpperCase()}`;
    try {
      const { data } = await api.post(`/programma/${w.data}/allinea`, { employee_id: w.employee_id, commessa_codice: w.commessa_codice, crea_programma: w.stato === "no_programma" });
      toast.success(`Programma del ${w.data.slice(8, 10)}/${w.data.slice(5, 7)} aggiornato → ${data.sede_nome}`);
      setProgWarn((m) => { const c = { ...m }; delete c[k]; return c; });
      setProgRecheck((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento del programma non riuscito");
    }
  };
  // Iter 131: budget ore delle commesse usate nel rapportino (ore residue visibili a chi compila)
  const [budgetInfo, setBudgetInfo] = useState([]);
  const codiciInForm = useMemo(() => {
    const set = new Set();
    for (const g of form.giorni || []) for (const r of g.righe || []) { const c = (r.commessa_codice || "").trim().toUpperCase(); if (c) set.add(c); }
    return [...set].sort();
  }, [form.giorni]);
  const codiciKey = codiciInForm.join(",");
  const hideBudget = user?.show_budget_rapportino === false; // Iter 140: disattivabile per utente
  useEffect(() => {
    if (!codiciKey || hideBudget) { setBudgetInfo([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/commesse/budget-status", { params: { codici: codiciKey } });
        setBudgetInfo(data?.items || []);
      } catch (_) { /* silenzioso */ }
    }, 800);
    return () => clearTimeout(h);
  }, [codiciKey, hideBudget]);
  const oreInFormPer = (codice) => {
    let tot = 0;
    for (const g of form.giorni || []) for (const r of g.righe || []) if ((r.commessa_codice || "").trim().toUpperCase() === codice) tot += Number(r.ore_tot) || 0;
    return Math.round(tot * 10) / 10;
  };
  const progDiscordanze = Object.values(progWarn).filter((w) => ["altro_cantiere", "non_assegnato", "no_programma"].includes(w.stato));
  const allineaTutte = async () => {
    for (const w of progDiscordanze) await allineaProgramma(w);
  };

  useEffect(() => {
    if (loading || isEdit || ocrData || draftChecked) return;
    setDraftChecked(true);
    if (new URLSearchParams(location.search).get("data")) return;
    const forceServer =
      new URLSearchParams(location.search).get("bozza") === "server";
    const isMeaningful = (f) =>
      (f?.giorni || [])
        .flatMap((g) => g.righe || [])
        .some(
          (r) =>
            (r.employee_nome || "").trim() ||
            Number(r.ore_tot) > 0 ||
            (r.commessa_codice || "").trim()
        );
    (async () => {
      try {
        let local = null;
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (isMeaningful(parsed?.form)) local = parsed;
          else localStorage.removeItem(DRAFT_KEY);
        }
        let server = null;
        if (forceServer || !local) {
          try {
            const { data } = await api.get("/bozze/mia");
            if (data?.form && isMeaningful(data.form))
              server = { form: data.form, at: data.updated_at };
          } catch (err) {
            console.warn("[draft] bozza server non disponibile:", err?.message);
          }
        }
        const chosen = forceServer ? server || local : local || server;
        if (!chosen) return;
        if (forceServer) {
          setForm(chosen.form);
          toast.success("Bozza ripresa: completa e salva il rapportino");
          return;
        }
        const when = chosen.at
          ? new Date(chosen.at).toLocaleString("it-IT", {
              day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
            })
          : "";
        if (
          window.confirm(
            `📝 Trovata una bozza di rapportino non salvata${when ? ` (${when})` : ""}.\n` +
              `Vuoi riprenderla e continuare da dove eri rimasto?`
          )
        ) {
          setForm(chosen.form);
          toast.success("Bozza ripresa: completa e salva il rapportino");
        } else {
          localStorage.removeItem(DRAFT_KEY);
          api.delete("/bozze/mia").catch(() => {});
        }
      } catch (err) {
        console.warn("[draft] ripristino fallito:", err?.message);
      }
    })();
    // eslint-disable-next-line
  }, [loading]);

  // Autosalvataggio bozza (debounce 800ms) ad ogni modifica del form
  useEffect(() => {
    if (loading || isEdit || !draftChecked) return;
    const t = setTimeout(() => {
      // Iter 49b: niente bozza se già salvato o se il form è ancora vuoto
      if (draftDoneRef.current || !formHasContent(form)) return;
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ form, at: new Date().toISOString() })
        );
      } catch (err) {
        console.warn("[draft] salvataggio bozza fallito:", err?.message);
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form, loading, draftChecked]);

  // Iter 43: sync bozza sul server (debounce 3s) → visibile agli admin
  const draftTimerRef = useRef(null);
  useEffect(() => {
    if (loading || isEdit || !draftChecked) return;
    draftTimerRef.current = setTimeout(() => {
      // Iter 49b: niente bozza se già salvato o se il form è ancora vuoto
      if (draftDoneRef.current || !formHasContent(form)) return;
      api.put("/bozze/mia", { form }).catch((err) => {
        console.warn("[draft] sync bozza server fallito:", err?.message);
      });
    }, 3000);
    return () => clearTimeout(draftTimerRef.current);
    // eslint-disable-next-line
  }, [form, loading, draftChecked]);

  // Iter 143: "non salvato" = c'è contenuto e non è ancora stato salvato (nuovo) o è stato modificato (edit)
  useEffect(() => {
    if (loading) return;
    if (!formInitRef.current) { formInitRef.current = true; return; }
    if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); }
    // eslint-disable-next-line
  }, [form]);
  const unsaved = !loading && !draftDoneRef.current && formHasContent(form) && (!isEdit || dirty);
  useEffect(() => {
    if (!unsaved) return;
    const h = (e) => { e.preventDefault(); e.returnValue = "Rapportino NON salvato: se esci perdi le modifiche."; return e.returnValue; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [unsaved]);
  const [exitAsk, setExitAsk] = useState(false);
  const pendingHrefRef = useRef(null);
  const onBackClick = (e) => {
    if (unsaved) { e.preventDefault(); pendingHrefRef.current = backTo; setExitAsk(true); }
  };
  // Iter 143: intercetta anche i link del menu in alto (stesso sito) mentre il rapportino non è salvato
  useEffect(() => {
    if (!unsaved) return;
    const h = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || a.target === "_blank" || a.dataset.testid === "back-arrow-link") return;
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("/") || href.startsWith("/rapportino/")) return;
      e.preventDefault(); e.stopPropagation();
      pendingHrefRef.current = href; setExitAsk(true);
    };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, [unsaved]);

  const employeeOptions = useMemo(
    () =>
      employees
        .filter((e) => e.attivo !== false)
        // Iter 30m: nascondi dipendenti marcati `mostra_nei_rapportini = false`
        // (es. impiegati che non vanno nei rapportini operativi).
        .filter((e) => e.mostra_nei_rapportini !== false)
        .map((e) => ({ id: e.id, label: `${e.cognome} ${e.nome}`, raw: e })),
    [employees]
  );

  useEffect(() => {
    const init = async () => {
      try {
        const [empRes, commRes] = await Promise.all([
          api.get("/employees"),
          api.get("/commesse"),
        ]);
        setEmployees(empRes.data || []);
        setCommesse(commRes.data || []);

        if (isEdit) {
          const { data } = await api.get(`/rapportini/${id}`);
          const giorni =
            data.giorni && data.giorni.length
              ? data.giorni.map((g) => ({
                  ...g,
                  righe: g.righe?.length ? g.righe : [newRiga()],
                }))
              : [emptyDay(data.data_inizio || data.data || todayIso())];
          setForm({
            numero: data.numero || 1,
            tipo: data.tipo === "officina" ? "officina" : "cantiere",
            data_inizio: data.data_inizio || data.data || todayIso(),
            data_fine: data.data_fine || data.data || todayIso(),
            giorni,
            foto: data.foto || [],
            firma: data.firma || "",
            firma_data: data.firma_data || "",
            firma_immagine: data.firma_immagine || "",
          });
          setOcrSources(data.ocr_sources || []);
          setLoadedVidimato(!!data.vidimato);
        } else if (ocrData && !ocrConfirmed) {
          // Keep showing the confirm dialog; pre-fill will happen on confirm
          // Auto-suggest next numero as fallback
          try {
            const { data: list } = await api.get("/rapportini");
            const max = (list || []).reduce(
              (m, r) => Math.max(m, Number(r.numero) || 0),
              0
            );
            setForm((f) => ({ ...f, numero: max + 1 }));
          } catch (e) {
            // Auto-suggest numero best-effort: se la lista fallisce, lasciamo il default
            console.warn("[rapportino] auto-suggest numero failed (ocr path):", e?.message);
          }
        } else {
          // Pre-fill: se URL ha ?data=YYYY-MM-DD (provenienza WeekView "Compila")
          const params = new URLSearchParams(location.search);
          const presetDate = params.get("data");
          const presetEmp = params.get("dipendente");
          if (presetDate) {
            const emp = (empRes.data || []).find((e) => e.id === presetEmp);
            const firstDay = emptyDay(presetDate);
            if (emp) {
              firstDay.righe[0] = { ...firstDay.righe[0], employee_id: emp.id, employee_nome: `${emp.cognome} ${emp.nome}` };
            }
            setForm((f) => ({
              ...f,
              data_inizio: presetDate,
              data_fine: presetDate,
              giorni: [firstDay],
            }));
          }
          // Auto-suggest next numero
          try {
            const { data: list } = await api.get("/rapportini");
            const max = (list || []).reduce(
              (m, r) => Math.max(m, Number(r.numero) || 0),
              0
            );
            setForm((f) => ({ ...f, numero: max + 1 }));
          } catch (e) {
            // Auto-suggest best-effort: usiamo numero=1 di default
            console.warn("[rapportino] auto-suggest numero failed (new path):", e?.message);
          }
          // Il caso "prefill da Salva e duplica" è gestito da un useEffect dedicato.
        }
      } catch (e) {
        toast.error("Errore caricamento");
      } finally {
        setLoading(false);
      }
    };
    init();
    // `init` riferisce variabili di setup (api, ocrData, location, navigate) tutte stabili o
    // intenzionalmente sotto controllo manuale: vogliamo re-inizializzare SOLO se cambia id/isEdit.
  }, [id, isEdit]);

  // Sync giorni when data_inizio / data_fine change.
  // FIX (iter 30g): NON ricreare i giorni perdendo le righe!
  // - Single-day: aggiorna solo la data del giorno esistente (mantiene righe).
  // - Multi-day: matching per INDICE posizionale (mantiene righe del giorno
  //   i-esimo anche se la data cambia). emptyDay solo per indici nuovi.
  const syncDateRange = (dataInizio, dataFine) => {
    const days = generateDays(dataInizio, dataFine);
    if (!days.length) return;
    const oldGiorni = form.giorni || [];
    const newGiorni = days.map((d, i) =>
      i < oldGiorni.length
        ? { ...oldGiorni[i], data: d }
        : emptyDay(d)
    );
    setForm({ ...form, data_inizio: dataInizio, data_fine: dataFine, giorni: newGiorni });
    setActiveDayIdx((idx) => Math.min(idx, newGiorni.length - 1));
  };

  const updateDay = (dayIdx, patch) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      giorni[dayIdx] = { ...giorni[dayIdx], ...patch };
      return { ...f, giorni };
    });
  };
  // Iter 132: il suffisso -OFF è sempre MAIUSCOLO e segue il tipo rapportino (officina → aggiunto, cantiere → tolto)
  const applyTipoToCode = (code, tipo) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) return c;
    if (tipo === "officina") return c.endsWith("-OFF") ? c : `${c}-OFF`;
    return c.endsWith("-OFF") ? c.slice(0, -4) : c;
  };
  const setTipoRapportino = (tipo) => {
    setForm((f) => ({
      ...f,
      tipo,
      giorni: (f.giorni || []).map((g) => ({
        ...g,
        righe: (g.righe || []).map((r) => (r.commessa_codice ? { ...r, commessa_codice: applyTipoToCode(r.commessa_codice, tipo), commessa_id: null } : r)),
      })),
    }));
  };
  const updateRiga = (dayIdx, rigaIdx, patch) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      const righe = [...giorni[dayIdx].righe];
      if (Object.prototype.hasOwnProperty.call(patch, "commessa_codice") && patch.commessa_codice) {
        const fixed = applyTipoToCode(patch.commessa_codice, f.tipo);
        if (fixed !== patch.commessa_codice) patch = { ...patch, commessa_codice: fixed, commessa_id: null };
      }
      righe[rigaIdx] = { ...righe[rigaIdx], ...patch };
      const r = righe[rigaIdx];
      const tot =
        Number(r.ore_guida || 0) +
        Number(r.ore_passegg || 0) +
        Number(r.ore_lavoro || 0) +
        Number(r.ore_caldo || 0) +
        Number(r.ore_notturne || 0);
      righe[rigaIdx].ore_tot = Math.round(tot * 100) / 100;
      giorni[dayIdx] = { ...giorni[dayIdx], righe };
      return { ...f, giorni };
    });
    // Auto-grow: se l'utente sta scrivendo nell'ULTIMA riga (employee o ore),
    // aggiungi automaticamente una riga vuota sotto per velocizzare l'inserimento.
    setTimeout(() => ensureTrailingEmpty(dayIdx), 0);
  };
  const addRiga = (dayIdx, opts = {}) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      const righe = giorni[dayIdx].righe;
      // Auto-fill commessa: copia l'ultima commessa valorizzata (la più recente
      // tra le righe del giorno) per velocizzare l'inserimento quando si
      // lavora su un solo cantiere.
      const lastWithCommessa = [...righe].reverse().find(
        (r) => (r.commessa_codice || "").trim() !== ""
      );
      // Iter 30n: copia anche il NOME del dipendente dalla riga sopra (l'ultima
      // riga con employee valorizzato). Doppio-click sul nome lo svuota.
      const lastWithEmployee = [...righe].reverse().find(
        (r) => (r.employee_nome || "").trim() !== "" || r.employee_id
      );
      const fresh = newRiga();
      if (lastWithCommessa && !opts.empty) {
        fresh.commessa_codice = lastWithCommessa.commessa_codice;
        fresh.commessa_id = lastWithCommessa.commessa_id || null;
      }
      if (lastWithEmployee && !opts.empty) {
        fresh.employee_nome = lastWithEmployee.employee_nome || "";
        fresh.employee_id = lastWithEmployee.employee_id || null;
      }
      giorni[dayIdx] = {
        ...giorni[dayIdx],
        righe: [...righe, fresh],
      };
      return { ...f, giorni };
    });
  };
  // Garantisce sempre una riga vuota alla fine (auto-grow Excel-like).
  // Iter 30n: "vuota" qui significa SENZA ORE e SENZA NOTE — il dipendente e
  // la commessa sono pre-compilati dalla riga sopra, quindi NON vanno
  // considerati come "contenuto utente" per il trigger dell'auto-grow.
  const ensureTrailingEmpty = (dayIdx) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      const righe = giorni[dayIdx].righe || [];
      const last = righe[righe.length - 1];
      const totOre =
        Number(last?.ore_lavoro || 0) +
        Number(last?.ore_guida || 0) +
        Number(last?.ore_passegg || 0) +
        Number(last?.ore_caldo || 0) +
        Number(last?.ore_notturne || 0);
      const lastHasContent =
        last && (totOre > 0 || (last.note || "").trim() !== "");
      if (!lastHasContent) return f;
      // Aggiungi riga con commessa e dipendente pre-compilati dalla riga sopra
      const fresh = newRiga();
      if ((last.commessa_codice || "").trim() !== "") {
        fresh.commessa_codice = last.commessa_codice;
        fresh.commessa_id = last.commessa_id || null;
      }
      if ((last.employee_nome || "").trim() !== "" || last.employee_id) {
        fresh.employee_nome = last.employee_nome || "";
        fresh.employee_id = last.employee_id || null;
      }
      giorni[dayIdx] = { ...giorni[dayIdx], righe: [...righe, fresh] };
      return { ...f, giorni };
    });
  };
  const removeRiga = (dayIdx, rigaIdx) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      if (giorni[dayIdx].righe.length === 1) return f;
      giorni[dayIdx] = {
        ...giorni[dayIdx],
        righe: giorni[dayIdx].righe.filter((_, i) => i !== rigaIdx),
      };
      return { ...f, giorni };
    });
  };
  // Iter 31y: sposta una riga su/giù all'interno dello stesso giorno.
  // Le frecce sono per click rapido; il drag-and-drop è gestito sotto.
  const moveRiga = (dayIdx, rigaIdx, direction) => {
    setForm((f) => {
      const giorni = [...f.giorni];
      const righe = [...(giorni[dayIdx].righe || [])];
      const newIdx = direction === "up" ? rigaIdx - 1 : rigaIdx + 1;
      if (newIdx < 0 || newIdx >= righe.length) return f;
      [righe[rigaIdx], righe[newIdx]] = [righe[newIdx], righe[rigaIdx]];
      giorni[dayIdx] = { ...giorni[dayIdx], righe };
      return { ...f, giorni };
    });
  };
  // Iter 31y: drag-and-drop. Usa l'API HTML5 nativa. Riordino solo entro
  // lo stesso giorno (non si possono spostare righe tra giorni diversi).
  const reorderRiga = (dayIdx, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setForm((f) => {
      const giorni = [...f.giorni];
      const righe = [...(giorni[dayIdx].righe || [])];
      if (fromIdx < 0 || fromIdx >= righe.length) return f;
      if (toIdx < 0 || toIdx >= righe.length) return f;
      const [moved] = righe.splice(fromIdx, 1);
      righe.splice(toIdx, 0, moved);
      giorni[dayIdx] = { ...giorni[dayIdx], righe };
      return { ...f, giorni };
    });
  };

  const totaleGiornata = (day) =>
    (day.righe || []).reduce((s, r) => s + Number(r.ore_tot || 0), 0);
  const totaleTotale = form.giorni.reduce(
    (s, g) => s + totaleGiornata(g),
    0
  );

  // Soglia ore eccessive da settings (default 13)
  const [overtimeThreshold, setOvertimeThreshold] = useState(13);
  // Iter 31b: ore arrotondate per difetto a step 0.50 — true se l'utente è
  // "amministrazione" o se nel pannello admin è attiva la modalità "8h per tutti".
  const [globalHoursCapEnabled, setGlobalHoursCapEnabled] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const v = parseFloat(data?.overtime_warning_threshold);
      if (!isNaN(v) && v > 0) setOvertimeThreshold(v);
      setGlobalHoursCapEnabled(!!data?.global_hours_cap_enabled);
    }).catch(() => { /* default 13 */ });
  }, []);
  // Iter 31e: niente più cap personale per "amministrazione" (utente
  // rimosso). Lo step intero si applica SOLO quando l'admin attiva la
  // modalità globale "8h per tutti" nelle Impostazioni.
  const forceHalfStep = globalHoursCapEnabled;

  // Calcolo violazioni "> Xh per dipendente/giorno" → warning UI
  const overtimeWarnings = (() => {
    const out = [];
    for (const g of form.giorni || []) {
      // Aggrega ore per employee_id (o nome se id mancante) nel giorno
      const byEmp = {};
      for (const r of g.righe || []) {
        const key =
          r.employee_id || (r.employee_nome || "").trim().toLowerCase();
        if (!key) continue;
        if (!byEmp[key]) {
          byEmp[key] = {
            label: r.employee_nome || "—",
            ore: 0,
            // Iter 107: ore già presenti in ALTRI rapportini dello stesso giorno
            altri: (dupWarn[`${g.data}||${key}`] || []).reduce((s2, x) => s2 + Number(x.ore || 0), 0),
            altriNumeri: (dupWarn[`${g.data}||${key}`] || []).map((x) => x.numero),
          };
        }
        byEmp[key].ore += Number(r.ore_tot || 0);
      }
      for (const k of Object.keys(byEmp)) {
        const tot = byEmp[k].ore + byEmp[k].altri;
        if (tot > overtimeThreshold) {
          out.push({
            data: g.data,
            employee: byEmp[k].label,
            ore: Math.round(tot * 10) / 10,
            qui: Math.round(byEmp[k].ore * 10) / 10,
            altri: Math.round(byEmp[k].altri * 10) / 10,
            altriNumeri: byEmp[k].altriNumeri,
          });
        }
      }
    }
    return out;
  })();

  const [confirmDuplicates, setConfirmDuplicates] = useState(null); // {duplicates, payload}

  // Definizione di "riga vuota": nessuna ora (tutte ore_* a 0) E nessuna nota.
  // Iter 30n: NON consideriamo più dipendente/commessa come "contenuto" perché
  // vengono pre-compilati dalla riga sopra (auto-fill). Il contenuto reale è
  // dato dalle ORE o dalla NOTA dell'utente.
  // Questo evita che le righe vuote generate dall'auto-grow finiscano salvate.
  const _isRigaEmpty = (r) => {
    if (!r) return true;
    const totOre =
      Number(r.ore_guida || 0) +
      Number(r.ore_passegg || 0) +
      Number(r.ore_lavoro || 0) +
      Number(r.ore_caldo || 0) +
      Number(r.ore_notturne || 0);
    const hasNote = (r.note || "").trim() !== "";
    return totOre === 0 && !hasNote;
  };

  const _buildPayload = () => ({
    numero: Number(form.numero),
    tipo: form.tipo || "cantiere",
    data_inizio: form.data_inizio,
    data_fine: form.data_fine,
    giorni: form.giorni.map((g) => ({
      ...g,
      tot_pasti: Number(g.tot_pasti) || 0,
      // Strip righe vuote (no dipendente AND no ore) prima di inviare al backend.
      righe: (g.righe || []).filter((r) => !_isRigaEmpty(r)),
    })),
    foto: form.foto || [],
    firma: form.firma || "",
    firma_data: form.firma_data || "",
    firma_immagine: form.firma_immagine || "",
  });

  // Iter 30u: avvisa l'utente se il rapportino appena salvato contiene una
  // data antecedente all'ultima esportazione per il gestionale. Backend
  // ritorna `warning_pre_export: {last_export_date, earliest_date_in_rapportino}`
  // quando applicabile, altrimenti null/assente.
  const _maybeWarnPreExport = (responseData) => {
    if (!responseData || !responseData.warning_pre_export) return;
    const w = responseData.warning_pre_export;
    const fmt = (iso) => {
      try {
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
      } catch (_) { return iso; }
    };
    toast.warning(
      `⚠️ Rapportino antecedente all'ultima esportazione gestionale ` +
        `(${fmt(w.last_export_date)}). Data più vecchia nel rapportino: ` +
        `${fmt(w.earliest_date_in_rapportino)}. Verifica di re-esportare per ` +
        `non perdere queste ore nel prossimo invio.`,
      { duration: 12000 }
    );
  };


  const _doSave = async (payload, opts = {}) => {
    // Iter 31nn: modifica di un rapportino GIÀ VIDIMATO (solo admin arriva
    // qui: il backend blocca gli altri) → conferma esplicita.
    if (isEdit && loadedVidimato) {
      const ok = window.confirm(
        "⚠️ ATTENZIONE: stai per modificare un rapportino GIÀ VIDIMATO.\n\n" +
          "Le ore ufficiali (statistiche, stampe ed eventuali esportazioni al " +
          "gestionale) cambieranno di conseguenza.\n\nVuoi procedere comunque?"
      );
      if (!ok) return;
    }
    setSaving(true);
    clearTimeout(draftTimerRef.current); // Iter 143: nessuna PUT bozza "in volo" dopo il salvataggio
    try {
      let result;
      if (isEdit) result = await api.put(`/rapportini/${id}`, payload);
      else result = await api.post("/rapportini", payload);
      // Salvataggio riuscito (anche in coda offline): la bozza non serve più.
      // Iter 49b: va eliminata anche la bozza SUL SERVER, altrimenti resta
      // in "Bozze" e sembra un rapportino duplicato con la stessa data.
      if (!isEdit) {
        draftDoneRef.current = true;
        localStorage.removeItem(DRAFT_KEY);
        api.delete("/bozze/mia").catch(() => {});
      }
      dirtyRef.current = false;
      setDirty(false);
      formInitRef.current = false;
      if (result?.queued || result?.data?._queued) {
        toast.warning(
          "Sei offline. Salvato localmente, sincronizzazione automatica al ritorno online."
        );
        return;
      }
      if (opts.duplicateNext) {
        // Costruisce e SALVA SUBITO il duplicato al giorno dopo
        const dupPayload = await _buildDuplicatePayload(payload);
        try {
          const dupResult = await api.post("/rapportini", dupPayload);
          if (dupResult?.queued || dupResult?.data?._queued) {
            toast.warning(
              "Duplicato salvato localmente, sincronizzazione automatica al ritorno online."
            );
            return;
          }
          toast.success(
            "Rapportino salvato e duplicato al giorno dopo: ora stai modificando il duplicato"
          );
          navigate(`/rapportino/${dupResult.data.id}`);
          return;
        } catch (errDup) {
          console.error("[rapportino] duplicate save failed:", errDup);
          toast.error(
            "Il rapportino è stato salvato, ma la duplicazione al giorno dopo non è riuscita"
          );
          navigate(`/rapportino/${result.data.id}`);
          return;
        }
      }
      if (opts.newSameDate) {
        // Apre un nuovo rapportino VUOTO con la stessa data (per cantieri/squadre
        // diverse nello stesso giorno). Il numero viene rigenerato in /new.
        const savedDate = payload?.data_inizio || (payload?.giorni?.[0]?.data || "");
        toast.success("Rapportino salvato. Nuovo modulo pronto per la stessa data.");
        _maybeWarnPreExport(result?.data);
        // Iter 49b: il nuovo modulo vuoto riattiva l'autosalvataggio bozza
        draftDoneRef.current = false;
        navigate(`/rapportino/new${savedDate ? `?data=${savedDate}` : ""}`);
        return;
      }
      toast.success(
        result?.data?.numero
          ? `Rapportino #${result.data.numero} salvato`
          : "Rapportino salvato"
      );
      _maybeWarnPreExport(result?.data);
      // Preserva la provenienza (?from=da-vidimare) dopo il salvataggio
      navigate(
        `/rapportino/${result.data.id}${backTo === "/da-vidimare" ? "?from=da-vidimare" : ""}`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Salvataggio non riuscito",
        { duration: 8000 }
      );
    } finally {
      setSaving(false);
      setConfirmDuplicates(null);
    }
  };

  // Costruisce il payload del rapportino duplicato: copia identica
  // del rapportino appena salvato ma con tutte le date spostate di +1 giorno
  // e numero auto-incrementato.
  const _buildDuplicatePayload = async (payload) => {
    // Shift +1 giorno timezone-safe (interpretando la data come "civile" UTC,
    // così evitiamo il bug per cui in CEST/CET il giorno tornava indietro).
    const shift = (iso) => {
      if (!iso) return iso;
      const parts = iso.split("-").map(Number);
      if (parts.length !== 3 || parts.some(isNaN)) return iso;
      const [y, m, d] = parts;
      const dt = new Date(Date.UTC(y, m - 1, d));
      dt.setUTCDate(dt.getUTCDate() + 1);
      const yy = dt.getUTCFullYear();
      const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(dt.getUTCDate()).padStart(2, "0");
      return `${yy}-${mm}-${dd}`;
    };
    // Auto-suggest numero del duplicato (max dei numeri esistenti + 1).
    // Fallback: numero corrente + 1.
    let nextNumero = (Number(payload.numero) || 0) + 1;
    try {
      const { data: list } = await api.get("/rapportini");
      const max = (list || []).reduce(
        (m, r) => Math.max(m, Number(r.numero) || 0), 0
      );
      nextNumero = max + 1;
    } catch (e) {
      console.warn("[rapportino] auto-numero per duplicato fallito:", e?.message);
    }
    return {
      numero: nextNumero,
      data_inizio: shift(payload.data_inizio),
      data_fine: shift(payload.data_fine),
      giorni: (payload.giorni || []).map((g) => ({
        ...g,
        data: shift(g.data),
      })),
      foto: payload.foto || [],
      firma: payload.firma || "",
      firma_data: payload.firma_data || "",
      firma_immagine: payload.firma_immagine || "",
    };
  };

  const handleSave = async (opts = {}) => {
    const payload = _buildPayload();
    // FIX iter 30h: blocca il salvataggio se ZERO ore totali (cioè nessuna
    // riga utile dopo lo strip delle vuote, oppure righe senza ore).
    const totaleOre = (payload.giorni || []).reduce(
      (s, g) =>
        s +
        (g.righe || []).reduce((sr, r) => sr + Number(r.ore_tot || 0), 0),
      0
    );
    if (totaleOre <= 0) {
      toast.error(
        "Impossibile salvare un rapportino con 0 ore. Compila almeno una riga con dipendente e ore."
      );
      return;
    }
    // Iter 35b: blocca il salvataggio (TUTTI, admin compreso) se una riga usa
    // un dipendente o una commessa NON esistenti in anagrafica, o un codice
    // fuori formato.
    {
      const problems = [];
      const codesSet = new Set(
        commesse.map((c) => (c.codice || "").trim().toUpperCase())
      );
      // Iter 65: commesse con note obbligatorie
      const noteReqByCode = new Map();
      for (const c of commesse) {
        const flag = c.note_obbligatorie || "";
        if (flag) noteReqByCode.set((c.codice || "").trim().toUpperCase(), flag);
      }
      // Iter 67: commesse chiuse (blocco dalla data di chiusura in poi)
      const chiuseByCode = new Map();
      for (const c of commesse) {
        if (c.chiusa && c.chiusa_dal) {
          chiuseByCode.set((c.codice || "").trim().toUpperCase(), c.chiusa_dal);
        }
      }
      const fmt = commessaCodiceRe();
      for (const g of payload.giorni || []) {
        for (const r of g.righe || []) {
          const nome = (r.employee_nome || "").trim();
          const code = (r.commessa_codice || "").trim();
          if (nome && !r.employee_id) {
            problems.push(
              `Il dipendente "${nome}" non esiste in anagrafica (${g.data}): selezionalo dai suggerimenti o crea prima la sua scheda`
            );
          }
          if (code) {
            const eff =
              (payload.tipo || form.tipo) === "officina" && !code.toUpperCase().endsWith("-OFF")
                ? `${code.toUpperCase()}-OFF`
                : code.toUpperCase();
            if (!fmt.test(eff)) {
              problems.push(
                `Codice commessa "${code}" non valido (${g.data}): il formato deve essere ${commessaEsempio()} oppure ${commessaEsempio()}-OFF`
              );
            } else if (!codesSet.has(eff)) {
              problems.push(
                `La commessa "${eff}" non esiste in anagrafica (${g.data}): creala prima di salvare`
              );
            } else {
              // Iter 65: note obbligatorie per questa commessa
              const flag = noteReqByCode.get(eff);
              if (flag && Number(r.ore_tot || 0) > 0) {
                const isFiglia = eff.endsWith("-OFF");
                const req =
                  flag === "entrambe" ||
                  (flag === "madre" && !isFiglia) ||
                  (flag === "figlia" && isFiglia);
                if (req && (r.note || "").trim().length < 10) {
                  problems.push(
                    `${r.employee_nome || "Riga"} · ${eff} (${g.data}): per questa commessa le NOTE sono OBBLIGATORIE (minimo 10 caratteri)`
                  );
                }
              }
              // Iter 67: commessa chiusa — niente ore dalla data di chiusura in poi
              const chiusaDal = chiuseByCode.get(eff);
              if (
                chiusaDal &&
                Number(r.ore_tot || 0) > 0 &&
                g.data &&
                g.data >= chiusaDal
              ) {
                problems.push(
                  `${r.employee_nome || "Riga"} · ${eff} (${g.data}): la commessa è CHIUSA dal ${chiusaDal.slice(8, 10)}/${chiusaDal.slice(5, 7)}/${chiusaDal.slice(2, 4)} — non si possono più inserire ore da quella data in poi`
                );
              }
            }
          }
        }
      }
      if (problems.length > 0) {
        toast.error("Impossibile salvare il rapportino", {
          description: "• " + problems.join("\n• "),
          duration: 12000,
        });
        return;
      }
    }
    // Avviso overtime: lascia comunque salvare, ma se vuoto siamo a posto
    if (overtimeWarnings.length > 0) {
      const list = overtimeWarnings
        .map((w) => `${w.employee} (${w.data}: ${w.ore}h${w.altri > 0 ? ` = ${w.qui}h qui + ${w.altri}h nei rapportini N° ${w.altriNumeri.join(", ")}` : ""})`)
        .join("\n• ");
      const ok = window.confirm(
        `Attenzione: questi dipendenti superano le ${overtimeThreshold} ore in un giorno:\n\n• ${list}\n\nSalvare comunque?`
      );
      if (!ok) return;
    }
    // Check duplicati (employee, data, commessa) verso DB
    try {
      const pairs = [];
      for (const g of payload.giorni) {
        for (const r of g.righe || []) {
          if (!g.data) continue;
          if (r.employee_id || r.employee_nome) {
            pairs.push({
              employee_id: r.employee_id || null,
              employee_nome: r.employee_nome || "",
              data: g.data,
              commessa_codice: r.commessa_codice || "",
            });
          }
        }
      }
      if (pairs.length > 0) {
        const { data: chk } = await api.post("/rapportini/duplicates-check", {
          pairs,
          exclude_rapportino_id: isEdit ? id : null,
        });
        if ((chk?.duplicates || []).length > 0) {
          setConfirmDuplicates({ duplicates: chk.duplicates, payload, opts });
          return;
        }
      }
    } catch (e) {
      console.warn("[rapportino] duplicates check failed:", e?.message);
    }
    await _doSave(payload, opts);
  };

  const handleSignImage = (dataUrl) => {
    if (!dataUrl) {
      // clear
      setForm({ ...form, firma_immagine: "", firma: "", firma_data: "" });
      return;
    }
    setForm({
      ...form,
      firma_immagine: dataUrl,
      firma: user?.name || user?.email || "Utente",
      firma_data: new Date().toISOString(),
    });
    toast.success("Rapportino firmato");
  };
  const handleClearSign = () =>
    setForm({ ...form, firma: "", firma_data: "", firma_immagine: "" });

  const handleSendEmail = async () => {
    if (!isEdit) {
      toast.error("Salva il rapportino prima di inviarlo");
      return;
    }
    setSendingEmail(true);
    try {
      const payload = {
        numero: Number(form.numero),
        data_inizio: form.data_inizio,
        data_fine: form.data_fine,
        giorni: form.giorni.map((g) => ({
          ...g,
          tot_pasti: Number(g.tot_pasti) || 0,
        })),
        foto: form.foto || [],
        firma: form.firma || "",
        firma_data: form.firma_data || "",
        firma_immagine: form.firma_immagine || "",
      };
      try {
        await api.put(`/rapportini/${id}`, payload);
      } catch (e) {
        // Iter 73: rapportino non più modificabile (es. vidimato) o permessi
        // limitati → invia comunque la versione già salvata invece di bloccare.
        console.warn("[email] pre-save fallito, invio la versione salvata:", e?.response?.data?.detail || e?.message);
      }
      const { data } = await api.post(`/rapportini/${id}/send-email`, {});
      if (data.status === "sent") {
        toast.success(`Email inviata a ${(data.recipients || []).join(", ")}`);
      } else if (data.status === "demo") {
        toast.info("Modalità demo: l'invio email è disabilitato");
      } else if (data.status === "partial") {
        toast.warning(
          `Email inviata a ${(data.recipients || []).join(", ")} — alcuni destinatari non raggiunti (verifica il dominio su Resend)`
        );
      } else if (data.status === "mocked") {
        toast.warning(
          "Email simulata: configurare RESEND_API_KEY per l'invio reale"
        );
      } else if (data.status === "skipped") {
        toast.error(
          "Nessun destinatario configurato. Vai su Impostazioni."
        );
      } else {
        toast.error(data.message || "Invio non riuscito");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio email non riuscito");
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-slate-500">Caricamento…</div>;
  }

  const isMultiDay = form.giorni.length > 1;
  const activeDay = form.giorni[activeDayIdx] || form.giorni[0];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <OcrConfirmDialog
        open={!!pendingOcr && !ocrConfirmed}
        ocrData={pendingOcr}
        employees={employees}
        onCancel={() => {
          setPendingOcr(null);
          navigate("/scansiona", { replace: true });
        }}
        onConfirm={(resolved) => {
          // Apply resolved OCR data to form
          const giorni =
            resolved.giorni && resolved.giorni.length
              ? resolved.giorni.map((g) => ({
                  data: g.data || todayIso(),
                  righe: g.righe?.length
                    ? g.righe.map((r) => ({ ...newRiga(), ...r }))
                    : [newRiga()],
                  annotazioni: g.annotazioni || "",
                  tot_pasti: g.tot_pasti || 0,
                  pasti_dettaglio: g.pasti_dettaglio || "",
                  mezzi_guidatori: g.mezzi_guidatori || "",
                  pranzo: g.pranzo || "",
                  pernotto: g.pernotto || "",
                }))
              : [emptyDay(todayIso())];
          const days = giorni.map((g) => g.data).filter(Boolean).sort();
          setForm((f) => ({
            ...f,
            numero: resolved.numero || f.numero,
            data_inizio:
              resolved.data_inizio || days[0] || todayIso(),
            data_fine:
              resolved.data_fine || days[days.length - 1] || todayIso(),
            giorni,
            foto: resolved.foto && resolved.foto.length ? resolved.foto : f.foto,
          }));
          setOcrConfirmed(true);
          setPendingOcr(null);
          toast.success(
            "Pre-compilato. Rivedi le ore e salva quando sei pronto.",
            { duration: 4000 }
          );
        }}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={backTo} data-testid="back-arrow-link" onClick={onBackClick}>
            <Button variant="outline" size="icon" className="h-11 w-11">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <UnsavedExitDialog open={exitAsk} onClose={() => setExitAsk(false)} onSave={() => { setExitAsk(false); handleSave(); }} onExit={() => { setExitAsk(false); draftDoneRef.current = false; navigate(pendingHrefRef.current || backTo); }} isEdit={isEdit} />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              {isEdit ? "Modifica" : "Nuovo"}
            </div>
            <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
              Rapportino interno di lavoro
            </h1>
            {forceHalfStep && (
              <div
                className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900"
                data-testid="half-step-badge"
                title="Modalità admin '8h per tutti' attiva: ore arrotondate per difetto a numeri interi (niente decimali) e cappate a 8h/giorno."
              >
                ⏱ Modalità 8h attiva — solo numeri interi
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isEdit && !user?.hours_cap && (
            <Link to={`/rapportino/${id}/stampa`}>
              <Button variant="outline" className="h-12">
                <Printer className="h-4 w-4 mr-2" /> {t("rapp.print")}
              </Button>
            </Link>
          )}
          {!isViewer && unsaved && (
            <span className="inline-flex items-center gap-1.5 rounded-md border-2 border-red-400 bg-red-50 px-2.5 py-1.5 text-xs font-black uppercase tracking-wide text-red-700 animate-pulse" data-testid="unsaved-badge" title="Il rapportino non è ancora salvato: è solo una bozza automatica. Premi Salva.">
              <AlertTriangle className="h-4 w-4" /> Non salvato
            </span>
          )}
          {!isViewer && (
            <Button
              data-testid="save-rapportino-button"
              onClick={() => handleSave()}
              disabled={saving}
              className={`h-12 ${unsaved ? "bg-red-600 hover:bg-red-700 ring-4 ring-red-200" : "bg-[#0044FF] hover:bg-[#0033CC]"}`}
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? t("rapp.saving") : t("rapp.save")}
            </Button>
          )}
        </div>
      </div>

      {/* Iter 31nn: avviso modifica rapportino già vidimato */}
      {isEdit && loadedVidimato && !isViewer && (
        <Card
          className="p-3 border-2 border-amber-400 bg-amber-50"
          data-testid="vidimato-warning-banner"
        >
          <div className="text-sm font-bold text-amber-900">
            ⚠️ Rapportino GIÀ VIDIMATO: qualsiasi modifica cambierà le ore
            ufficiali (statistiche, stampe, export gestionale).
          </div>
        </Card>
      )}

      {/* Banner sola visualizzazione */}
      {isViewer && (
        <Card
          className="p-3 border-amber-300 bg-amber-50"
          data-testid="viewer-readonly-banner"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <Eye className="h-4 w-4 shrink-0" />
            Account in sola visualizzazione — le modifiche sono disabilitate.
          </div>
        </Card>
      )}

      {/* Overtime warning >13h */}
      {overtimeWarnings.length > 0 && (
        <Card
          className="p-3 md:p-4 border-amber-300 bg-amber-50"
          data-testid="overtime-warning-banner"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-amber-900 mb-1">
                Attenzione: oltre {overtimeThreshold} ore in un giorno (tutti i rapportini del dipendente compresi)
              </div>
              <ul className="space-y-0.5">
                {overtimeWarnings.map((w, i) => (
                  <li key={`${w.employee}-${w.data}-${i}`} className="text-amber-800">
                    <b>{w.employee}</b> · {w.data} → <b>{w.ore}h</b>
                    {w.altri > 0 && (
                      <span className="text-xs"> ({w.qui}h in questo rapportino + {w.altri}h già nei rapportini N° {w.altriNumeri.join(", ")})</span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="text-xs text-amber-700 mt-1.5">
                Verifica i dati prima di salvare.
              </div>
            </div>
          </div>
        </Card>
      )}


      {/* OCR pre-fill banner */}
      {ocrInfo && (
        <Card
          className="p-4 bg-gradient-to-r from-[#F0F4FF] to-white border-[#0044FF]/30"
          data-testid="ocr-banner"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-[#0044FF] text-white grid place-items-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-sm">
                Modulo pre-compilato dalla scansione AI
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Rivedi attentamente i dati estratti, correggi nomi/ore se
                necessario, poi clicca <b>Salva</b>.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-bold">
                  ✓ {ocrInfo.matched_employees.filter((m) => m.matched).length}/
                  {ocrInfo.matched_employees.length} dipendenti riconosciuti
                </span>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-bold">
                  ✓ {ocrInfo.matched_commesse.filter((m) => m.matched).length}/
                  {ocrInfo.matched_commesse.length} commesse abbinate
                </span>
                {ocrInfo.matched_employees.filter((m) => !m.matched).length > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-bold inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {ocrInfo.matched_employees.filter((m) => !m.matched).length}{" "}
                    nomi non in anagrafica:{" "}
                    {ocrInfo.matched_employees
                      .filter((m) => !m.matched)
                      .map((m) => m.raw)
                      .slice(0, 3)
                      .join(", ")}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOcrInfo(null)}
              data-testid="dismiss-ocr-banner"
              className="h-8"
            >
              ✕
            </Button>
          </div>
        </Card>
      )}

      {/* Tipo rapportino: Cantiere vs Officina */}
      <Card className={`p-4 ${isViewer ? "pointer-events-none" : ""}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
            Tipo rapportino:
          </Label>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" data-testid="tipo-rapportino-toggle">
            <button
              type="button"
              onClick={() => setTipoRapportino("cantiere")}
              className={`px-4 py-2 text-sm font-bold transition-colors ${
                form.tipo !== "officina"
                  ? "bg-[#0044FF] text-white"
                  : "bg-white text-slate-700 hover:bg-slate-100"
              }`}
              data-testid="tipo-cantiere-button"
            >
              CANTIERE
            </button>
            <button
              type="button"
              onClick={() => setTipoRapportino("officina")}
              className={`px-4 py-2 text-sm font-bold transition-colors border-l border-slate-300 ${
                form.tipo === "officina"
                  ? "bg-amber-600 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-100"
              }`}
              data-testid="tipo-officina-button"
            >
              OFFICINA
            </button>
          </div>
          {form.tipo === "officina" && (
            <p className="text-xs text-amber-700 font-medium">
              Le commesse del rapportino hanno il suffisso <b>-OFF</b> (sotto-commessa di officina), aggiunto in automatico.
            </p>
          )}
          {!isViewer && (
            <label className="ml-auto flex items-center gap-2 cursor-pointer select-none" title="Se attivo, mentre compili confronta ogni riga col Programma giornaliero e ti propone di spostare la persona nel cantiere giusto" data-testid="prog-sync-toggle">
              <Switch checked={progSync} onCheckedChange={toggleProgSync} data-testid="prog-sync-switch" />
              <span className="text-xs font-bold text-slate-700 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-[#0044FF]" /> Sincronizza col programma</span>
            </label>
          )}
        </div>
      </Card>

      {/* Header fields */}
      <Card className={`p-5 ${isViewer ? "pointer-events-none" : ""}`}>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-4">
          <div className="col-span-1 md:col-span-3">
            <Label className="text-xs font-bold uppercase tracking-wide">
              Rapp. N°
            </Label>
            <Input
              type="number"
              min="1"
              value={form.numero}
              onChange={(e) => setForm({ ...form, numero: e.target.value })}
              className="h-12 text-lg font-heading font-bold mt-1"
              data-testid="numero-rapportino-input"
            />
          </div>
          <div className="col-span-1 md:col-span-9">
            <Label className="text-xs font-bold uppercase tracking-wide flex items-center gap-1">
              <CalendarRange className="h-3 w-3" /> Data
            </Label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <div className="relative flex-1 min-w-[160px]">
                <CalIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                  type="date"
                  value={form.data_inizio}
                  onChange={(e) =>
                    syncDateRange(e.target.value, e.target.value)
                  }
                  className="h-12 pl-10 w-full min-w-0"
                  data-testid="data-inizio-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const t = todayIso();
                  syncDateRange(t, t);
                }}
                data-testid="oggi-button"
                className="h-12 shrink-0 border-slate-300 hover:bg-slate-50"
                title="Imposta la data al giorno corrente"
              >
                Oggi
              </Button>
            </div>
            {/* Visual feedback: giorno della settimana e warning data lontana */}
            <DateInfoLine isoDate={form.data_inizio} />
          </div>
        </div>
        {isMultiDay && (
          <div className="text-xs text-amber-700 mt-3 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠ Questo rapportino è multi-giorno legacy ({form.giorni.length} giorni · totale{" "}
            <b>{totaleTotale.toFixed(2)} ore</b>). Per nuovi rapportini usa una sola data —
            per più cantieri/giorni crea rapportini separati.
          </div>
        )}
      </Card>

      {budgetInfo.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 space-y-1" data-testid="budget-banner">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Budget ore commesse</div>
          {budgetInfo.map((b) => {
            const inForm = isEdit ? 0 : oreInFormPer(b.codice);
            const residuo = Math.round((b.residuo - inForm) * 10) / 10;
            const pct = b.budget_ore ? Math.round(((b.ore_usate + inForm) / b.budget_ore) * 100) : 0;
            const tone = pct >= 100 ? "text-red-700 bg-red-50 border-red-200" : pct >= 80 ? "text-amber-800 bg-amber-50 border-amber-200" : "text-emerald-800 bg-emerald-50 border-emerald-200";
            return (
              <div key={b.codice} className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs border rounded px-2 py-1 ${tone}`} data-testid={`budget-row-${b.codice}`}>
                <span className="font-mono font-bold">{b.codice}</span>
                <span>budget <b>{b.budget_ore} h</b></span>
                <span>usate <b>{b.ore_usate} h</b>{inForm > 0 ? ` (+${inForm} h in questo rapportino)` : ""}</span>
                <span className="font-bold">{residuo >= 0 ? `restano ${residuo} h` : `SUPERATO di ${Math.abs(residuo)} h`}</span>
                <span className="ml-auto font-mono">{pct}%</span>
              </div>
            );
          })}
        </div>
      )}
      {progDiscordanze.length > 1 && !isViewer && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 flex flex-wrap items-center gap-2 text-sm text-amber-900" data-testid="programma-discordanze-banner">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1"><b>{progDiscordanze.length} persone</b> in questo rapportino risultano altrove (o non previste) nel Programma giornaliero.</span>
          <Button type="button" size="sm" variant="outline" onClick={allineaTutte} className="border-amber-400 text-amber-900 hover:bg-amber-100" data-testid="programma-allinea-tutte">Allinea tutte nel programma</Button>
        </div>
      )}
      {/* Tabella stile-PDF (single-day) */}
      {form.giorni.map((day, dayIdx) => (
        <div
          key={day.data + dayIdx}
          className={`space-y-4 ${isViewer ? "pointer-events-none" : ""}`}
        >
          <Card className="p-0 overflow-hidden">
            <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between">
              <div className="font-heading font-bold tracking-tight">
                {dayLabel(day.data)} — Commessa, Personale e Ore
              </div>
              <div className="text-sm font-mono">
                Totale: <span className="font-bold">{totaleGiornata(day).toFixed(2)} h</span>
              </div>
            </div>

            <RigheTable
              day={day}
              dayIdx={dayIdx}
              commesse={commesse}
              employeeOptions={employeeOptions}
              updateRiga={updateRiga}
              removeRiga={removeRiga}
              moveRiga={moveRiga}
              reorderRiga={reorderRiga}
              addRiga={addRiga}
              halfStep={forceHalfStep}
              dupWarn={dupWarn}
              overtimeThreshold={overtimeThreshold}
              dupKeyOf={dupKeyOf}
              progWarn={progWarn}
              progKeyOf={progKeyOf}
              onAllinea={allineaProgramma}
              onCommessaCreated={(c) => setCommesse((prev) => [...prev, c])}
              onEmployeeCreated={(emp) =>
                setEmployees((prev) => [...prev, emp])
              }
              canCreateEmployee={user?.role === "admin" || !!user?.can_add_employees}
              employeeRequiresConfirmation={user?.role !== "admin"}
            />
          </Card>

          {/* Per-day fields (annotazioni / pasti / mezzi / pranzo / pernotto) */}
          <Card className="p-5 space-y-4">
            <div className="font-heading font-bold tracking-tight">
              Annotazioni del {dayLabel(day.data)}
            </div>
            <Textarea
              rows={3}
              value={day.annotazioni || ""}
              onChange={(e) => updateDay(dayIdx, { annotazioni: e.target.value })}
              placeholder="Annotazioni del giorno…"
              data-testid={`annotazioni-input-${dayIdx}`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Tot. pasti</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={day.tot_pasti || 0}
                  onChange={(e) => updateDay(dayIdx, { tot_pasti: e.target.value })}
                  className="h-12 mt-1"
                  data-testid={`tot-pasti-input-${dayIdx}`}
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Mezzi / Guidatori</Label>
                <Input
                  value={day.mezzi_guidatori || ""}
                  onChange={(e) => updateDay(dayIdx, { mezzi_guidatori: e.target.value })}
                  placeholder="Es: c17 — Stefano; c23 — Antonio"
                  className="h-12 mt-1"
                  data-testid={`mezzi-input-${dayIdx}`}
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Pranzo</Label>
                <Input
                  value={day.pranzo || ""}
                  onChange={(e) => updateDay(dayIdx, { pranzo: e.target.value })}
                  placeholder="Es: Flunch"
                  className="h-12 mt-1"
                  data-testid={`pranzo-input-${dayIdx}`}
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide">Pernotto</Label>
                <Input
                  value={day.pernotto || ""}
                  onChange={(e) => updateDay(dayIdx, { pernotto: e.target.value })}
                  placeholder="Es: Hotel F1 Chambery"
                  className="h-12 mt-1"
                  data-testid={`pernotto-input-${dayIdx}`}
                />
              </div>
            </div>
          </Card>
        </div>
      ))}

      {/* Iter 46: riepilogo live "totale ore per dipendente" del rapportino */}
      {form.giorni.some((g) => (g.righe || []).some((r) => Number(r.ore_tot) > 0)) && (
        <Card className="p-4 border-2 border-blue-200 bg-blue-50/50">
          <EmployeeTotals
            righe={form.giorni.flatMap((g) => g.righe || [])}
            testId="form-employee-totals"
          />
        </Card>
      )}

      {/* Allegati: nascosti per utenti con hours_cap (es. amministrazione).
          Per il Visualizzatore richiedono lo sblocco con password. */}
      {isViewer ? (
        !user?.view_unlocked ? (
          <ViewerUnlockCard
            title="Allegati e firme protetti"
            description="Inserisci di nuovo la tua password per vedere foto, firme e allegati."
            onUnlocked={() => window.location.reload()}
          />
        ) : (
          <div className="space-y-6 pointer-events-none">
            <OcrSourceGallery
              sources={ocrSources}
              apiBase={process.env.REACT_APP_BACKEND_URL}
            />
            <Card className="p-5 space-y-3">
              <div className="font-heading font-bold tracking-tight flex items-center gap-2">
                <Camera className="h-5 w-5" /> Foto del cantiere
              </div>
              {(form.foto || []).length === 0 ? (
                <div className="text-sm text-slate-500">Nessuna foto allegata</div>
              ) : (
                <PhotoUploader
                  paths={form.foto || []}
                  rapportinoId={isEdit ? id : null}
                  onChange={() => {}}
                  max={10}
                />
              )}
            </Card>
            <Card className="p-5 space-y-3">
              <div className="font-heading font-bold tracking-tight flex items-center gap-2">
                <PenLine className="h-5 w-5" /> Firma del compilatore
              </div>
              {form.firma_immagine ? (
                <div className="border-2 border-green-200 bg-green-50 rounded-md p-3">
                  <img
                    src={form.firma_immagine}
                    alt="Firma"
                    className="max-h-32 bg-white rounded mx-auto block"
                    data-testid="firma-preview-img"
                  />
                  <div className="mt-2 text-center">
                    <div className="font-bold text-sm">Firmato da {form.firma}</div>
                    <div className="text-xs text-slate-600">
                      Il{" "}
                      {form.firma_data
                        ? new Date(form.firma_data).toLocaleString("it-IT")
                        : ""}
                    </div>
                  </div>
                </div>
              ) : form.firma ? (
                <div className="text-sm">
                  Firmato da <b>{form.firma}</b>
                </div>
              ) : (
                <div className="text-sm text-slate-500">Nessuna firma presente</div>
              )}
            </Card>
          </div>
        )
      ) : !user?.hours_cap && (
        <>
          {/* Sorgente OCR (cartaceo originale) — solo se importato via OCR */}
          <OcrSourceGallery
            sources={ocrSources}
            apiBase={process.env.REACT_APP_BACKEND_URL}
          />

          {/* Foto (shared) */}
          <Card className="p-5 space-y-3">
            <div className="font-heading font-bold tracking-tight flex items-center gap-2">
              <Camera className="h-5 w-5" /> Foto del cantiere
              <span className="text-xs font-normal text-slate-500">
                (max 10, 5 MB ciascuna · condivise per la settimana)
              </span>
            </div>
            <PhotoUploader
              paths={form.foto || []}
              rapportinoId={isEdit ? id : null}
              onChange={(paths) => setForm({ ...form, foto: paths })}
              max={10}
            />
          </Card>

      {/* Firma (shared) */}
      <Card className="p-5 space-y-3">
        <div className="font-heading font-bold tracking-tight flex items-center gap-2">
          <PenLine className="h-5 w-5" /> Firma del compilatore
        </div>
        {form.firma_immagine ? (
          <div className="space-y-3">
            <div className="border-2 border-green-200 bg-green-50 rounded-md p-3">
              <img
                src={form.firma_immagine}
                alt="Firma"
                className="max-h-32 bg-white rounded mx-auto block"
                data-testid="firma-preview-img"
              />
              <div className="mt-2 text-center">
                <div className="font-bold text-sm">Firmato da {form.firma}</div>
                <div className="text-xs text-slate-600">
                  Il{" "}
                  {form.firma_data
                    ? new Date(form.firma_data).toLocaleString("it-IT")
                    : ""}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSign}
                data-testid="clear-firma-button"
                className="h-10"
              >
                Rimuovi firma
              </Button>
            </div>
          </div>
        ) : form.firma ? (
          // Legacy text-only signature (compatibility for old rapportini)
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border border-green-200 bg-green-50 rounded-md p-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
              <div>
                <div className="font-bold text-sm">Firmato da {form.firma}</div>
                <div className="text-xs text-slate-600">
                  Il{" "}
                  {form.firma_data
                    ? new Date(form.firma_data).toLocaleString("it-IT")
                    : ""}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearSign}
              data-testid="clear-firma-button"
              className="h-10"
            >
              Rimuovi firma
            </Button>
          </div>
        ) : (
          <SignaturePad onChange={handleSignImage} />
        )}
      </Card>
        </>
      )}

      {/* Email */}
      {isEdit && !user?.hours_cap && !isViewer && (
        <Card className="p-5 space-y-3">
          <div className="font-heading font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-5 w-5" /> Invia per email
          </div>
          <p className="text-sm text-slate-600">
            Invia il rapportino in formato PDF (1 pagina per giorno) ai
            destinatari configurati nelle Impostazioni.
          </p>
          <Button
            type="button"
            onClick={handleSendEmail}
            disabled={sendingEmail}
            data-testid="send-email-button"
            variant="outline"
            className="h-12"
          >
            {sendingEmail ? (
              "Invio in corso…"
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" /> Invia rapportino via email
              </>
            )}
          </Button>
        </Card>
      )}

      <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3 pt-2">
        <Link to={backTo}>
          <Button variant="outline" className="h-12 w-full md:w-auto">
            {isViewer ? "Torna indietro" : "Annulla"}
          </Button>
        </Link>
        {!isViewer && (
          <>
            <Button
              onClick={() => handleSave({ newSameDate: true })}
              disabled={saving}
              variant="outline"
              data-testid="save-and-new-same-date-button"
              title="Salva il rapportino e ne apre uno nuovo VUOTO con la stessa data, comodo per inserire un altro cantiere/squadra dello stesso giorno."
              className="h-12 border-emerald-500 text-emerald-700 hover:bg-emerald-50 w-full md:w-auto"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t("rapp.saveNew")}
            </Button>
            <Button
              onClick={() => handleSave({ duplicateNext: true })}
              disabled={saving}
              variant="outline"
              data-testid="save-and-duplicate-next-button"
              title="Salva il rapportino e ne apre uno nuovo, identico, con la data spostata al giorno successivo. Potrai modificarlo prima di salvare."
              className="h-12 border-[#0044FF] text-[#0044FF] hover:bg-[#0044FF]/5 w-full md:w-auto"
            >
              <CopyPlus className="h-5 w-5 mr-2" />
              {t("rapp.saveDup")}
            </Button>
            <Button
              onClick={() => handleSave()}
              disabled={saving}
              data-testid="save-rapportino-button-bottom"
              className="h-12 bg-[#0044FF] hover:bg-[#0033CC] w-full md:w-auto"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? t("rapp.saving") : t("rapp.saveFull")}
            </Button>
          </>
        )}
      </div>

      {/* Dialog conferma duplicati — dettaglio Esistente vs Nuovo per riga */}
      {confirmDuplicates && (
        <DuplicateChoiceDialog
          state={confirmDuplicates}
          saving={saving}
          onCancel={() => setConfirmDuplicates(null)}
          onConfirm={(filteredPayload) =>
            _doSave(filteredPayload, confirmDuplicates.opts || {})
          }
        />
      )}
    </div>
  );
}

// --- Dialog scelta duplicati: utente decide quali righe in conflitto tenere ---
function DuplicateChoiceDialog({ state, saving, onCancel, onConfirm }) {
  // Per ogni conflitto del backend (employee+data+commessa) trovo la riga
  // corrispondente nel payload nuovo, così posso mostrare "Esistente 12h vs Nuovo 8h"
  const conflicts = useMemo(() => {
    const out = [];
    for (const d of state.duplicates) {
      const dataIso = d.data;
      const empKey =
        d.employee_id || (d.employee_nome || "").trim().toLowerCase();
      const codeNew = (d.input_commessa_codice || "").toLowerCase();
      let newRiga = null;
      let dayIdx = -1, rigaIdx = -1;
      const giorni = state.payload.giorni || [];
      for (let gi = 0; gi < giorni.length; gi++) {
        if ((giorni[gi].data || "") !== dataIso) continue;
        for (let ri = 0; ri < (giorni[gi].righe || []).length; ri++) {
          const r = giorni[gi].righe[ri];
          const rkey =
            r.employee_id || (r.employee_nome || "").trim().toLowerCase();
          const rcode = (r.commessa_codice || "").toLowerCase();
          if (rkey === empKey && (codeNew === "" || rcode === codeNew)) {
            newRiga = r;
            dayIdx = gi;
            rigaIdx = ri;
            break;
          }
        }
        if (newRiga) break;
      }
      out.push({ d, newRiga, dayIdx, rigaIdx });
    }
    return out;
  }, [state]);

  // Default: tutte le righe in conflitto vengono TENUTE (salva tutto)
  const [discarded, setDiscarded] = useState(new Set());
  const toggleDiscard = (idx) => {
    setDiscarded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleConfirm = () => {
    if (discarded.size === 0) {
      onConfirm(state.payload);
      return;
    }
    // Build set of (dayIdx, rigaIdx) da scartare
    const toRemove = new Set();
    for (const idx of discarded) {
      const c = conflicts[idx];
      if (c && c.dayIdx >= 0 && c.rigaIdx >= 0) {
        toRemove.add(`${c.dayIdx}|${c.rigaIdx}`);
      }
    }
    const filtered = {
      ...state.payload,
      giorni: (state.payload.giorni || []).map((g, gi) => ({
        ...g,
        righe: (g.righe || []).filter((_, ri) => !toRemove.has(`${gi}|${ri}`)),
      })),
    };
    onConfirm(filtered);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-2 md:p-6"
      data-testid="duplicate-confirm-dialog"
    >
      <Card className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <AlertCircle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">
              Stessa commessa già inserita ({conflicts.length})
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Per questo dipendente esiste già un rapportino con la{" "}
              <strong>stessa commessa nello stesso giorno</strong>. Decidi cosa
              tenere e cosa scartare. (Più cantieri/commesse diverse nello stesso
              giorno sono permesse — solo gli stessi (dipendente + data +
              commessa) duplicati vengono segnalati qui.)
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {conflicts.map((c, idx) => {
            const isDiscard = discarded.has(idx);
            const sameCommessa = c.d.match_type === "commessa";
            return (
              <div
                key={`${c.d.rapportino_id}-${c.d.data}-${c.d.commessa_codice}-${idx}`}
                className={`border-2 rounded-lg overflow-hidden ${
                  isDiscard ? "border-slate-300 opacity-60" : "border-amber-300"
                }`}
                data-testid={`dup-conflict-${idx}`}
              >
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-baseline justify-between gap-2">
                  <div className="font-bold text-sm">
                    {c.d.employee_nome || c.newRiga?.employee_nome || "—"}
                  </div>
                  <div className="text-xs text-slate-600">
                    {c.d.data}{" "}
                    {sameCommessa && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded text-[10px] font-bold tracking-wider">
                        STESSA COMMESSA
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                  {/* Esistente */}
                  <div className="p-3 bg-amber-50/50">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">
                      Esistente — rapportino #{c.d.numero}
                    </div>
                    <div className="text-sm">
                      <div>
                        Commessa:{" "}
                        <b className="font-mono">{c.d.commessa_codice || "—"}</b>
                      </div>
                      <div>
                        Ore: <b>{Number(c.d.ore_tot || 0).toFixed(1)}h</b>
                      </div>
                    </div>
                  </div>
                  {/* Nuovo */}
                  <div className={`p-3 ${isDiscard ? "" : "bg-blue-50/50"}`}>
                    <div
                      className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${
                        isDiscard ? "text-slate-500" : "text-blue-700"
                      }`}
                    >
                      Nuovo {isDiscard ? "(SCARTATO)" : ""}
                    </div>
                    <div className="text-sm">
                      <div>
                        Commessa:{" "}
                        <b className="font-mono">
                          {c.newRiga?.commessa_codice || c.d.input_commessa_codice || "—"}
                        </b>
                      </div>
                      <div>
                        Ore:{" "}
                        <b>{Number(c.newRiga?.ore_tot || 0).toFixed(1)}h</b>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-3 py-2 bg-slate-50 border-t border-slate-200">
                  <Button
                    size="sm"
                    variant={isDiscard ? "outline" : "default"}
                    onClick={() => discarded.has(idx) && toggleDiscard(idx)}
                    className={`h-8 ${
                      !isDiscard ? "bg-blue-600 hover:bg-blue-700 text-white" : ""
                    }`}
                    data-testid={`dup-keep-${idx}`}
                  >
                    Tieni nuovo
                  </Button>
                  <Button
                    size="sm"
                    variant={isDiscard ? "default" : "outline"}
                    onClick={() => !discarded.has(idx) && toggleDiscard(idx)}
                    className={`h-8 ${
                      isDiscard ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-600"
                    }`}
                    data-testid={`dup-discard-${idx}`}
                  >
                    Scarta nuovo
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <div className="text-sm text-slate-700 flex-1">
            <b className="text-blue-700">{conflicts.length - discarded.size}</b>{" "}
            tenute · <b className="text-red-700">{discarded.size}</b> scartate
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="duplicate-cancel-button"
            >
              Annulla tutto
            </Button>
            <Button
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              onClick={handleConfirm}
              disabled={saving}
              data-testid="duplicate-confirm-button"
            >
              Salva con queste scelte
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Iter 31d: arrotondamento per difetto al numero INTERO (no decimali).
// Es: 1.20 → 1, 1.99 → 1, 7.99 → 7, 0.4 → 0.
// Sostituisce il vecchio step 0.50 (Iter 31b) — richiesta utente: nessun
// decimale quando è attiva la modalità 8h o per l'account amministrazione.
function floorToHalf(v) {
  const f = Number(v || 0);
  if (!isFinite(f) || f <= 0) return 0;
  return Math.floor(f);
}

function OreField({ label, value, onChange, testId, halfStep = false }) {
  return (
    <div>
      <Label className="text-xs font-bold">{label}</Label>
      <Input
        type="number"
        step={halfStep ? "1" : "0.5"}
        min="0"
        value={value === 0 ? "" : value}
        onChange={(e) =>
          onChange(e.target.value === "" ? 0 : Number(e.target.value))
        }
        onBlur={(e) => {
          if (!halfStep) return;
          if (e.target.value === "" || e.target.value == null) return;
          const rounded = floorToHalf(Number(e.target.value));
          if (rounded !== Number(e.target.value)) onChange(rounded);
        }}
        className="h-12 mt-1 text-base"
        data-testid={testId}
        placeholder="0"
        title={halfStep ? "Step intero — niente decimali" : undefined}
      />
    </div>
  );
}

// --- DateInfoLine: feedback visivo per il campo Data ---
// Iter 30l: mostra giorno della settimana sotto al campo data, e warning se
// la data scelta è molto distante da oggi (>= 90 giorni avanti o indietro).
// Aiuta a prevenire errori di selezione del datepicker (es. mese sbagliato).
function DateInfoLine({ isoDate }) {
  if (!isoDate) return null;
  let d;
  try {
    d = new Date(isoDate + "T12:00:00");
    if (isNaN(d.getTime())) return null;
  } catch {
    return null;
  }
  const dayName = d.toLocaleDateString("it-IT", { weekday: "long" });
  const dateLabel = d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diffMs = d.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const farFuture = diffDays >= 90;
  const farPast = diffDays <= -90;
  const isFar = farFuture || farPast;
  // Capitalizza prima lettera del giorno
  const dayCap = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
  return (
    <div className="mt-1.5 space-y-1" data-testid="date-info-line">
      <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
        <span>
          📅 <strong>{dayCap}</strong>, {dateLabel}
        </span>
        {isWeekend && (
          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
            Weekend
          </span>
        )}
      </div>
      {isFar && (
        <div
          className="text-xs px-2 py-1.5 rounded border border-amber-300 bg-amber-50 text-amber-900 flex items-start gap-2"
          data-testid="date-far-warning"
        >
          <span className="text-base leading-none">⚠</span>
          <div>
            <strong>Data lontana da oggi.</strong>{" "}
            {farFuture
              ? `Sono ${diffDays} giorni nel futuro.`
              : `Sono ${Math.abs(diffDays)} giorni nel passato.`}{" "}
            Controlla bene il <strong>mese</strong> e l&apos;
            <strong>anno</strong> prima di salvare.
          </div>
        </div>
      )}
    </div>
  );
}

// --- Tabella stile-PDF per le righe del giorno ---
// Layout: COMMESSA | PERSONALE | GUIDA | PASSEGG | LAVORO | 🔥 | 🌙 | TOT | NOTE | ✕
// - Auto-grow: appena scrivi in qualcosa nell'ultima riga vuota viene aggiunta
//   automaticamente una riga sotto (gestito a livello di pagina via ensureTrailingEmpty)
// - Pre-fill commessa: alla creazione manuale di una nuova riga, copia la commessa
//   dell'ultima riga valorizzata
function RigheTable({
  day,
  dayIdx,
  commesse,
  employeeOptions,
  updateRiga,
  removeRiga,
  moveRiga,
  reorderRiga,
  addRiga,
  onCommessaCreated,
  onEmployeeCreated,
  halfStep = false,
  dupWarn = {},
  dupKeyOf = () => null,
  progWarn = {},
  progKeyOf = () => null,
  onAllinea = () => {},
  overtimeThreshold = 13,
  canCreateEmployee = false,
  employeeRequiresConfirmation = false,
}) {
  const righe = day.righe || [];
  const { t } = useTranslation("app");

  // Iter 103: numeri dei rapportini già esistenti per (giorno+dipendente).
  const rigaDup = (riga) => {
    const k = dupKeyOf(day.data, riga);
    return k && dupWarn[k]?.length ? dupWarn[k] : null;
  };
  // Iter 119: avviso Programma per la riga
  const rigaProg = (riga) => { const k = progKeyOf(day.data, riga); return k ? progWarn[k] || null : null; };
  const progText = (w) => {
    const d = `${w.data.slice(8, 10)}/${w.data.slice(5, 7)}`;
    if (w.stato === "altro_cantiere") return { t: `Nel programma del ${d} era a ${w.sede_attuale}, non a ${w.sede_target?.nome}`, b: "Conferma: spostalo nel programma", cls: "text-amber-800 bg-amber-50 border-amber-200" };
    if (w.stato === "non_assegnato") return { t: `Nel programma del ${d} era ${w.sede_attuale ? `in "${w.sede_attuale}"` : "non assegnato"}`, b: `Aggiungilo a ${w.sede_target?.nome}`, cls: "text-amber-800 bg-amber-50 border-amber-200" };
    if (w.stato === "no_programma") return { t: `Nessun programma per il ${d}`, b: `Crea il programma e mettilo a ${w.sede_target?.nome}`, cls: "text-sky-800 bg-sky-50 border-sky-200" };
    if (w.stato === "assente") return { t: `Nel programma del ${d} risulta in ${(w.assenza_tipo || "assenza").toLowerCase()}: verifica`, b: null, cls: "text-red-800 bg-red-50 border-red-200" };
    if (w.stato === "sede_non_trovata") return { t: `Commessa ${w.commessa_codice} non collegata a nessuna sede del programma (Risorse → Sedi)`, b: null, cls: "text-slate-600 bg-slate-50 border-slate-200" };
    return null;
  };
  const ProgNotice = ({ riga, tid }) => {
    const w = rigaProg(riga); const p = w && progText(w);
    if (!p) return null;
    return (
      <div className={`mt-1 text-[11px] font-semibold border rounded px-1.5 py-1 space-y-1 ${p.cls}`} data-testid={tid}>
        <div className="flex items-start gap-1 leading-snug"><MapPin className="h-3 w-3 shrink-0 mt-0.5" /><span>{p.t}</span></div>
        {p.b && <button type="button" onClick={() => onAllinea(w)} className="w-full rounded bg-white border border-current px-1.5 py-0.5 font-bold hover:bg-slate-50 text-left" data-testid={`${tid}-confirm`}>✓ {p.b}</button>}
      </div>
    );
  };
  // Iter 107: testo avviso con le ore già registrate + totale giornata
  const dupText = (riga) => {
    const hits = rigaDup(riga) || [];
    const altri = hits.reduce((s2, x) => s2 + Number(x.ore || 0), 0);
    const k = dupKeyOf(day.data, riga);
    const qui = righe.filter((r) => dupKeyOf(day.data, r) === k).reduce((s2, r) => s2 + Number(r.ore_tot || 0), 0);
    const tot = Math.round((qui + altri) * 10) / 10;
    const list = hits.map((x) => `N° ${x.numero} (${Math.round(x.ore * 10) / 10} h)`).join(", ");
    return { text: `Già nel rapportino ${list} per questo giorno`, tot, over: overtimeThreshold > 0 && tot > overtimeThreshold };
  };

  // Iter 86: commesse delle righe sovrastanti, per copia rapida senza ridigitare
  const prevCommesse = (rigaIdx) => {
    const cur = (righe[rigaIdx]?.commessa_codice || "").trim().toUpperCase();
    const seen = [];
    for (let i = rigaIdx - 1; i >= 0; i--) {
      const c = (righe[i].commessa_codice || "").trim();
      if (!c) continue;
      const u = c.toUpperCase();
      if (u === cur || seen.some((s) => s.codice.toUpperCase() === u)) continue;
      seen.push({ codice: c, id: righe[i].commessa_id || null });
    }
    return seen.slice(0, 3);
  };

  const copyChips = (rigaIdx, suffix = "", inline = false) => {
    if (rigaIdx === 0) return null;
    const prev = prevCommesse(rigaIdx);
    if (!prev.length) return null;
    const n = prev.length;
    // Iter 91: adattivo — più chips = carattere più piccolo e codice abbreviato
    const fontCls = !inline || n === 1 ? "text-[10px]" : n === 2 ? "text-[9px]" : "text-[8px]";
    const short = (c) =>
      inline && n > 1 ? c.split("-").slice(1).join("-") : c;
    return (
      <div
        className={
          inline
            ? "flex items-center gap-0.5 min-w-0 overflow-hidden"
            : "flex flex-wrap gap-1 mt-1"
        }
      >
        {prev.map((p) => (
          <button
            type="button"
            key={p.codice}
            onClick={() =>
              updateRiga(dayIdx, rigaIdx, {
                commessa_codice: p.codice,
                commessa_id: p.id,
              })
            }
            className={`inline-flex items-center min-w-0 ${fontCls} font-mono font-bold px-1 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 active:scale-95 transition-colors`}
            data-testid={`riga-${dayIdx}-${rigaIdx}-copy-commessa-${p.codice}${suffix}`}
            title={t("rapp.copiaCommessa", { code: p.codice })}
          >
            <ChevronRight className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{short(p.codice)}</span>
          </button>
        ))}
      </div>
    );
  };
  // Iter 31y: stato locale per drag-and-drop riordinamento.
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Helper: handler drag&drop riusabile sia desktop sia mobile-card.
  const dragHandlers = (rigaIdx) => ({
    draggable: true,
    onDragStart: (e) => {
      setDragIdx(rigaIdx);
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(rigaIdx));
      } catch (err) {
        console.debug("[dnd] dataTransfer non supportato:", err?.message);
      }
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOverIdx !== rigaIdx) setDragOverIdx(rigaIdx);
    },
    onDragLeave: () => {
      if (dragOverIdx === rigaIdx) setDragOverIdx(null);
    },
    onDrop: (e) => {
      e.preventDefault();
      const from = dragIdx;
      setDragIdx(null);
      setDragOverIdx(null);
      if (from === null || from === rigaIdx) return;
      reorderRiga && reorderRiga(dayIdx, from, rigaIdx);
    },
    onDragEnd: () => {
      setDragIdx(null);
      setDragOverIdx(null);
    },
  });

  // Totali colonna (per riga totali-giornata in fondo)
  const tot = righe.reduce(
    (acc, r) => {
      acc.guida += Number(r.ore_guida || 0);
      acc.passegg += Number(r.ore_passegg || 0);
      acc.lavoro += Number(r.ore_lavoro || 0);
      acc.caldo += Number(r.ore_caldo || 0);
      acc.notturne += Number(r.ore_notturne || 0);
      acc.tot += Number(r.ore_tot || 0);
      return acc;
    },
    { guida: 0, passegg: 0, lavoro: 0, caldo: 0, notturne: 0, tot: 0 }
  );

  return (
    <div className="w-full">
      {/* Desktop / tablet: tabella vera e propria */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-100 border-b border-slate-300">
            <tr className="text-[11px] uppercase tracking-wider text-slate-700">
              <th className="px-2 py-2 text-left font-bold w-[18%] border-r border-slate-200">
                {t("rapp.commessa")}
              </th>
              <th className="px-2 py-2 text-left font-bold w-[20%] border-r border-slate-200">
                {t("rapp.personale")}
              </th>
              <th className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200">
                {t("rapp.guida")}
              </th>
              <th className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200">
                {t("rapp.passegg")}
              </th>
              <th className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200">
                {t("rapp.lavoro")}
              </th>
              <th
                className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200"
                title="Ore caldo"
              >
                <Flame className="h-3.5 w-3.5 inline text-orange-600" />
              </th>
              <th
                className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200"
                title="Ore notturne"
              >
                <Moon className="h-3.5 w-3.5 inline text-indigo-600" />
              </th>
              <th className="px-1 py-2 text-center font-bold w-[7%] border-r border-slate-200">
                {t("rapp.tot")}
              </th>
              <th className="px-2 py-2 text-left font-bold w-[16%] border-r border-slate-200">
                {t("rapp.note")}
              </th>
              <th className="px-1 py-2 text-center font-bold w-[4%]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {righe.map((riga, rigaIdx) => (
              <tr
                key={riga._uid || `${dayIdx}-${rigaIdx}`}
                className={`hover:bg-slate-50/60 align-top transition-colors ${
                  dragIdx === rigaIdx ? "opacity-40" : ""
                } ${
                  dragOverIdx === rigaIdx && dragIdx !== rigaIdx
                    ? "bg-blue-50 outline outline-2 outline-blue-300"
                    : ""
                }`}
                data-testid={`riga-row-${dayIdx}-${rigaIdx}`}
                {...dragHandlers(rigaIdx)}
              >
                {/* Commessa */}
                <td className="px-1 py-1.5 border-r border-slate-200 align-middle">
                  <div className="flex items-center gap-1 w-full min-w-0">
                    {copyChips(rigaIdx, "", true)}
                    <div className="flex-1 min-w-[96px]">
                      <CommessaCombobox
                        testId={`riga-${dayIdx}-${rigaIdx}-commessa`}
                        commesse={commesse}
                        value={riga.commessa_codice}
                        giornoData={day.data || ""}
                        compact
                        onSelect={(c) =>
                          updateRiga(dayIdx, rigaIdx, {
                            commessa_codice: c.codice || "",
                            commessa_id: c.id || null,
                          })
                        }
                        onCreated={onCommessaCreated}
                      />
                    </div>
                  </div>
                </td>
                {/* Personale */}
                <td className="px-1 py-1.5 border-r border-slate-200 align-middle">
                  <EmployeeAutocomplete
                    testId={`riga-${dayIdx}-${rigaIdx}-personale`}
                    items={employeeOptions}
                    value={riga.employee_nome}
                    compact
                    onChange={(v) =>
                      updateRiga(dayIdx, rigaIdx, {
                        employee_nome: v,
                        employee_id: null,
                      })
                    }
                    onSelect={(it) =>
                      updateRiga(dayIdx, rigaIdx, {
                        employee_nome: it.label,
                        employee_id: it.id,
                      })
                    }
                    onCreated={(emp) => {
                      // Iter 30q: aggiunge il dipendente appena creato alla
                      // lista in memoria così l'autocomplete lo trova anche
                      // nelle righe successive senza reload.
                      onEmployeeCreated?.(emp);
                    }}
                    canCreate={canCreateEmployee}
                    requiresConfirmation={employeeRequiresConfirmation}
                  />
                  {rigaDup(riga) && (
                    <div
                      className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                      data-testid={`riga-${dayIdx}-${rigaIdx}-dup-warning`}
                      title={`Esiste già un rapportino per questo dipendente in data ${day.data}`}
                    >
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {dupText(riga).text}
                        {dupText(riga).over && <b className="text-red-700"> · totale giornata {dupText(riga).tot} h &gt; {overtimeThreshold} h</b>}
                      </span>
                    </div>
                  )}
                  <ProgNotice riga={riga} tid={`riga-${dayIdx}-${rigaIdx}-prog-warning`} />
                </td>
                {/* Ore — input compatto centrato */}
                {[
                  ["ore_guida", "guida"],
                  ["ore_passegg", "passegg"],
                  ["ore_lavoro", "lavoro"],
                  ["ore_caldo", "caldo"],
                  ["ore_notturne", "notturne"],
                ].map(([field, slug]) => (
                  <td
                    key={field}
                    className="px-1 py-1.5 border-r border-slate-200 align-middle text-center"
                  >
                    <Input
                      type="number"
                      step={halfStep ? "1" : "0.5"}
                      min="0"
                      value={riga[field] === 0 ? "" : riga[field]}
                      onChange={(e) =>
                        updateRiga(dayIdx, rigaIdx, {
                          [field]:
                            e.target.value === "" ? 0 : Number(e.target.value),
                        })
                      }
                      onBlur={(e) => {
                        if (!halfStep) return;
                        if (e.target.value === "" || e.target.value == null) return;
                        const rounded = floorToHalf(Number(e.target.value));
                        if (rounded !== Number(e.target.value)) {
                          updateRiga(dayIdx, rigaIdx, { [field]: rounded });
                        }
                      }}
                      placeholder="0"
                      data-testid={`riga-${dayIdx}-${rigaIdx}-ore-${slug}`}
                      className="h-9 text-center text-sm px-1 font-mono"
                      title={halfStep ? "Step intero — niente decimali" : undefined}
                    />
                  </td>
                ))}
                {/* TOT */}
                <td className="px-1 py-1.5 border-r border-slate-200 align-middle text-center font-mono font-bold text-base text-slate-900 bg-slate-50">
                  <span data-testid={`riga-${dayIdx}-${rigaIdx}-ore-tot`}>
                    {Number(riga.ore_tot || 0).toFixed(2)}
                  </span>
                </td>
                {/* Note */}
                <td className="px-1 py-1.5 border-r border-slate-200 align-middle">
                  <Input
                    value={riga.note || ""}
                    onChange={(e) =>
                      updateRiga(dayIdx, rigaIdx, { note: e.target.value })
                    }
                    placeholder="—"
                    data-testid={`riga-${dayIdx}-${rigaIdx}-note`}
                    className="h-9 text-sm px-2"
                  />
                </td>
                {/* Rimuovi + Riordino */}
                <td className="px-1 py-1.5 align-middle text-center">
                  <div className="flex items-center justify-center gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveRiga(dayIdx, rigaIdx, "up")}
                      disabled={rigaIdx === 0}
                      data-testid={`move-up-${dayIdx}-${rigaIdx}`}
                      className="h-7 w-7 p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                      title="Sposta su"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => moveRiga(dayIdx, rigaIdx, "down")}
                      disabled={rigaIdx === righe.length - 1}
                      data-testid={`move-down-${dayIdx}-${rigaIdx}`}
                      className="h-7 w-7 p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                      title="Sposta giù"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRiga(dayIdx, rigaIdx)}
                      disabled={righe.length === 1}
                      data-testid={`remove-riga-${dayIdx}-${rigaIdx}`}
                      className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                      title="Rimuovi riga"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {/* Totali giornata */}
            <tr className="bg-slate-900 text-white font-mono font-bold">
              <td
                className="px-2 py-2 text-right uppercase tracking-wider text-[11px]"
                colSpan={2}
                data-testid="totali-giornata-label"
              >
                Totali giornata
              </td>
              <td className="px-1 py-2 text-center">{tot.guida.toFixed(2)}</td>
              <td className="px-1 py-2 text-center">{tot.passegg.toFixed(2)}</td>
              <td className="px-1 py-2 text-center">{tot.lavoro.toFixed(2)}</td>
              <td className="px-1 py-2 text-center text-orange-300">
                {tot.caldo.toFixed(2)}
              </td>
              <td className="px-1 py-2 text-center text-indigo-300">
                {tot.notturne.toFixed(2)}
              </td>
              <td className="px-1 py-2 text-center bg-slate-800">
                {tot.tot.toFixed(2)}
              </td>
              <td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile: card per riga (più ergonomica del table) */}
      <div className="md:hidden divide-y divide-slate-200">
        {righe.map((riga, rigaIdx) => (
          <div
            key={riga._uid || `${dayIdx}-${rigaIdx}-m`}
            className={`p-3 space-y-2 transition-colors ${
              dragIdx === rigaIdx ? "opacity-40" : ""
            } ${
              dragOverIdx === rigaIdx && dragIdx !== rigaIdx
                ? "bg-blue-50 outline outline-2 outline-blue-300"
                : ""
            }`}
            data-testid={`riga-row-mobile-${dayIdx}-${rigaIdx}`}
            {...dragHandlers(rigaIdx)}
          >
            <div className="flex items-center justify-between gap-1">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Riga #{rigaIdx + 1}
              </div>
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveRiga(dayIdx, rigaIdx, "up")}
                  disabled={rigaIdx === 0}
                  data-testid={`move-up-mobile-${dayIdx}-${rigaIdx}`}
                  className="h-8 w-8 p-0 text-slate-500 disabled:opacity-30"
                  title="Sposta su"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveRiga(dayIdx, rigaIdx, "down")}
                  disabled={rigaIdx === righe.length - 1}
                  data-testid={`move-down-mobile-${dayIdx}-${rigaIdx}`}
                  className="h-8 w-8 p-0 text-slate-500 disabled:opacity-30"
                  title="Sposta giù"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRiga(dayIdx, rigaIdx)}
                  disabled={righe.length === 1}
                  data-testid={`remove-riga-${dayIdx}-${rigaIdx}`}
                  className="text-red-600 h-8 px-2"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold">{t("rapp.commessa").toUpperCase()}</Label>
              <CommessaCombobox
                testId={`riga-${dayIdx}-${rigaIdx}-commessa`}
                commesse={commesse}
                value={riga.commessa_codice}
                giornoData={day.data || ""}
                onSelect={(c) =>
                  updateRiga(dayIdx, rigaIdx, {
                    commessa_codice: c.codice || "",
                    commessa_id: c.id || null,
                  })
                }
                onCreated={onCommessaCreated}
              />
              {copyChips(rigaIdx, "-m")}
            </div>
            <div>
              <Label className="text-[10px] font-bold">{t("rapp.personale").toUpperCase()}</Label>
              <EmployeeAutocomplete
                testId={`riga-${dayIdx}-${rigaIdx}-personale`}
                items={employeeOptions}
                value={riga.employee_nome}
                onChange={(v) =>
                  updateRiga(dayIdx, rigaIdx, {
                    employee_nome: v,
                    employee_id: null,
                  })
                }
                onSelect={(it) =>
                  updateRiga(dayIdx, rigaIdx, {
                    employee_nome: it.label,
                    employee_id: it.id,
                  })
                }
                onCreated={(emp) =>
                  onEmployeeCreated?.(emp)
                }
                canCreate={canCreateEmployee}
                requiresConfirmation={employeeRequiresConfirmation}
              />
              {rigaDup(riga) && (
                <div
                  className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                  data-testid={`riga-m-${dayIdx}-${rigaIdx}-dup-warning`}
                >
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  <span>
                    {dupText(riga).text}
                    {dupText(riga).over && <b className="text-red-700"> · totale giornata {dupText(riga).tot} h &gt; {overtimeThreshold} h</b>}
                  </span>
                </div>
              )}
              <ProgNotice riga={riga} tid={`riga-m-${dayIdx}-${rigaIdx}-prog-warning`} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <OreField
                label="Guida"
                testId={`riga-${dayIdx}-${rigaIdx}-ore-guida`}
                value={riga.ore_guida}
                halfStep={halfStep}
                onChange={(v) => updateRiga(dayIdx, rigaIdx, { ore_guida: v })}
              />
              <OreField
                label="Passegg."
                testId={`riga-${dayIdx}-${rigaIdx}-ore-passegg`}
                value={riga.ore_passegg}
                halfStep={halfStep}
                onChange={(v) => updateRiga(dayIdx, rigaIdx, { ore_passegg: v })}
              />
              <OreField
                label="Lavoro"
                testId={`riga-${dayIdx}-${rigaIdx}-ore-lavoro`}
                value={riga.ore_lavoro}
                halfStep={halfStep}
                onChange={(v) => updateRiga(dayIdx, rigaIdx, { ore_lavoro: v })}
              />
              <OreField
                label={
                  <>
                    <Flame className="h-3 w-3 inline mr-1 text-orange-600" />
                    Caldo
                  </>
                }
                testId={`riga-${dayIdx}-${rigaIdx}-ore-caldo`}
                value={riga.ore_caldo}
                halfStep={halfStep}
                onChange={(v) => updateRiga(dayIdx, rigaIdx, { ore_caldo: v })}
              />
              <OreField
                label={
                  <>
                    <Moon className="h-3 w-3 inline mr-1 text-indigo-600" />
                    Nott.
                  </>
                }
                testId={`riga-${dayIdx}-${rigaIdx}-ore-notturne`}
                value={riga.ore_notturne}
                halfStep={halfStep}
                onChange={(v) => updateRiga(dayIdx, rigaIdx, { ore_notturne: v })}
              />
              <div>
                <Label className="text-[10px] font-bold">{t("rapp.tot").toUpperCase()}</Label>
                <Input
                  readOnly
                  value={Number(riga.ore_tot || 0).toFixed(2)}
                  className="h-12 mt-1 font-heading font-bold text-base bg-slate-100"
                  data-testid={`riga-${dayIdx}-${rigaIdx}-ore-tot`}
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold">{t("rapp.note").toUpperCase()}</Label>
              <Input
                value={riga.note || ""}
                onChange={(e) =>
                  updateRiga(dayIdx, rigaIdx, { note: e.target.value })
                }
                placeholder="—"
                data-testid={`riga-${dayIdx}-${rigaIdx}-note`}
                className="h-10 mt-1"
              />
            </div>
          </div>
        ))}
        {/* Totale mobile sotto le card */}
        <div className="bg-slate-900 text-white px-3 py-2.5 font-mono font-bold flex items-center justify-between text-sm">
          <span className="uppercase tracking-wider text-[11px]">
            Totale giornata
          </span>
          <span className="text-lg">{tot.tot.toFixed(2)} h</span>
        </div>
      </div>

      <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-3 flex-wrap">
        <div className="text-xs text-slate-500">
          💡 Le nuove righe ereditano la commessa dell&apos;ultima riga.
          Scrivi nell&apos;ultima riga e una riga vuota si aggiungerà automaticamente sotto.
        </div>
        <Button
          variant="outline"
          onClick={() => addRiga(dayIdx)}
          className="h-11 border-dashed shrink-0"
          data-testid={`add-riga-button-${dayIdx}`}
        >
          <Plus className="h-4 w-4 mr-2" /> {t("rapp.addRiga")}
        </Button>
      </div>
    </div>
  );
}
